<script setup lang="ts">
import WidgetRecordAction from '@components/widget/WidgetRecordAction.vue'

const { field, readonly, meta } = defineProps<{
  field: any
  readonly: boolean
  meta: any
}>()

const emit = defineEmits(['removeItem'])
const removeItem = (item: unknown) => emit('removeItem', item)

const OPERATIONS_TYPES = {
  operations_counterparty_in: 'Оприходование',
  operations_in: 'Возврат',
} as const

const FIELD_TYPES = {
  list_string: 'list_string',
  list: 'list',
  qty: 'qty'
} as const

const FIELD_MAP = {
  [FIELD_TYPES.list_string]: {
    title: 'Список инвентаризационных номеров (через запятую)',
    type: 'InventoryNumbers'
  },
  [FIELD_TYPES.list]: {
    title: 'Инвентаризационные номера',
    type: 'InventoryNumbersList'
  },
  [FIELD_TYPES.qty]: {
    title: 'Количество',
    type: 'Quantity'
  }
} as const

const getMaxQuantities = (uuid: string): string => {
  const maxQuantitiesToReturn = meta?.MaxQuantitiesToReturn

  return maxQuantitiesToReturn[uuid]
}

// Почему то в записях описаны не все доступные поля, переберем и подготовим
const prepareValues = (values) => {
  values.forEach(item => {
    // TODO: костыль, при добавлении МТО для списания - очистим поле Value (если Value массив)
    if (!readonly && Array.isArray(item.Fields)) {
      item.Fields.forEach(_field => {
        if (Array.isArray(_field.Value) && _field.Value.length && !_field.cleaned) {
          _field.Value = []
          _field.cleaned = true // помечаем, что уже почистили
        }
      })
    }

    if (item.Record?.FormType === FIELD_TYPES.list_string) {
      if (item.Fields.find((field) => field.Name === FIELD_MAP[FIELD_TYPES.list_string].type)) return

      item.Fields.push({
        Name: FIELD_MAP[FIELD_TYPES.list_string].type,
        Value: ''
      })
    } else if (item.Record?.FormType === FIELD_TYPES.list) {
      const field = item.Fields.find((field) => field.Name === FIELD_MAP[FIELD_TYPES.list].type)
      if (field) {
        // В режиме просмотра приходит в виде объекта, переведем в массив
        if (!Array.isArray(field.Value)) {
          field.Value = Object.values(field.Value || {})
        }
      } else {
        item.Fields.push({
          Name: FIELD_MAP[FIELD_TYPES.list].type,
          Value: []
        })
      }
    }
  })

  return values
}
</script>

<template>
  <div v-if="field?._values?.length">
    <VTable>
      <tbody>
        <tr
          v-for="item in prepareValues(field?._values)"
          :key="(item.Record || item)?.Bundle.Uuid"
        >
          <td>
            <RouterLink
              :to="{ name: 'logistic', params: { uuid: (item.Record || item)?.Bundle.Uuid } }"
              :target="readonly ? undefined : '_blank'"
            >
              {{ (item.Record || item)?.Name }}
            </RouterLink>
          </td>

          <td class="text-end">
            {{ FIELD_MAP[item.Record?.FormType]?.title }}:
          </td>

          <td>
            <div
              v-for="_field in item.Fields"
              :key="(item.Record || item)?.Name + '-' + _field.Name"
            >
              <template v-if="_field.Name === FIELD_MAP[item.Record?.FormType]?.type">
                <template v-if="item.Record?.FormType === FIELD_TYPES.list">
                  <template v-if="readonly">
                    <VList
                      v-if="_field.Value?.length"
                      density="compact"
                      variant="text"
                      bg-color="transparent"
                    >
                      <VListItem
                        v-for="value in (_field.Value || []).filter(v => v.Title)"
                        :key="value.id"
                        :title="value.Title"
                        class="px-0 py-0"
                      />
                    </VList>
                  </template>
                  <template v-else>
                    <VCheckbox
                      v-for="checkbox in _field.PossibleValues"
                      :key="checkbox.value"
                      v-model="_field.Value"
                      class="v-input__control mb-0 mt-0"
                      :label="checkbox.Title"
                      density="compact"
                      :value="checkbox"
                      return-object
                      :hide-details="true"
                      :multiple="true"
                    >
                      <template #label>
                        <VLabel
                          class="text-no-wrap"
                        >
                          {{ checkbox.Title }}
                        </VLabel>
                      </template>
                    </VCheckbox>
                  </template>
                </template>

                <template v-else-if="item.Record?.FormType === FIELD_TYPES.qty">
                  <span v-if="readonly">
                    {{ _field.Value }}
                  </span>
                  <VNumberInput
                    v-else
                    v-model="_field.Value"
                    control-variant="hidden"
                    density="compact"
                    :hide-details="true"
                  />
                </template>

                <template v-else-if="item.Record?.FormType === FIELD_TYPES.list_string">
                  <span v-if="readonly">
                    {{ _field.Value }}
                  </span>
                  <VTextField
                    v-else
                    v-model="_field.Value"
                    :hide-details="true"
                    type="text"
                    density="compact"
                    min-width="200"
                  />
                </template>
              </template>
            </div>
          </td>

          <td v-if="!readonly">
            <div
              v-if="item.Record?.OperationType === OPERATIONS_TYPES.operations_in"
              class="text-red"
            >
              не более: {{ getMaxQuantities(item.Record?.Bundle?.Uuid) }}
            </div>
            <div
              v-else
              class="text-red"
            >
              не более: {{ item.Record?.MaxQuantityToIssue }}
            </div>
          </td>

          <td
            v-if="!readonly"
            class="text-center"
          >
            <WidgetRecordAction
              :selected="true"
              view
              @delete="removeItem(item)"
            />
          </td>
        </tr>
      </tbody>
    </VTable>
  </div>
</template>

<style scoped>
.v-input__control {
  grid-area: auto;
}
</style>
